import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import { getPublicSubathon, Subathon } from '../../api/payment/subathon.api';
import { clockSkewMs, formatAdd, formatHms, isEndedVisible, remainingAt } from '../../utils/subathon';
import { createResponseGate } from '../../utils/response-gate';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';

/**
 * Overlay ĐẾM NGƯỢC SUBATHON — chèn vào OBS bằng Browser Source.
 *
 * Chạy KHÔNG đăng nhập: lấy subathon qua endpoint công khai, và nghe cùng hub SignalR mà
 * overlay thông báo donate đang dùng (/payment-hubs?userid=...). Đồng hồ đếm dựa trên
 * `serverNow` của máy chủ (không tự cộng dồn ở client) nên không lệch khi máy người xem sai
 * giờ hoặc bỏ lỡ sự kiện hub — mỗi lần có donate thì gọi lại BE lấy số đúng thay vì tự cộng.
 */
const OUTLINE =
  '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';

const FALLBACK_POLL_MS = 30_000;
const ADD_EFFECT_MS = 4_000;

interface AddEffect {
  label: string;
  name: string;
}

const PageSubathonLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  const clockColor = searchParams.get('color') || '39539E';
  const textColor = searchParams.get('textColor') || 'FFFFFF';

  const [sub, setSub] = useState<Subathon | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [addEffect, setAddEffect] = useState<AddEffect | null>(null);

  const skewRef = useRef(0);
  const gateRef = useRef(createResponseGate());
  const prevRef = useRef<{ id: string; addedSeconds: number } | null>(null);
  const zeroReloadedRef = useRef(false);
  const addEffectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Lịch tải lại lần 2 sau khi có donate: bản công khai cache tĩnh 3 giây phía BE, nên load() gọi
  // ngay lúc nhận sự kiện có thể vẫn trúng bản cache dựng TRƯỚC lúc donate được ghi nhận -> hiệu ứng
  // "+m:ss" bị nuốt mất. Lịch thêm 1 lần tải sau 3500ms (qua khỏi TTL cache) để chắc chắn có dữ liệu
  // mới; response gate (gateRef) đã tự lo thứ tự nên tải chồng lên nhau không sao.
  const secondLoadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!contentCreatorId) return;
    const token = gateRef.current.issue();
    try {
      const data = await getPublicSubathon(contentCreatorId);
      // Phản hồi về không theo thứ tự gửi (hub + poll dự phòng + tải lại xác nhận Ended có thể
      // chồng nhau) -> chỉ áp phản hồi mới nhất, tránh đè trạng thái mới bằng dữ liệu cũ.
      if (!gateRef.current.acceptRead(token)) return;

      skewRef.current = clockSkewMs(data.serverNow);

      const prev = prevRef.current;
      if (
        prev &&
        prev.id === data.id &&
        data.addedSeconds > prev.addedSeconds &&
        data.lastAdd
      ) {
        setAddEffect({
          label: formatAdd(data.addedSeconds - prev.addedSeconds),
          name: data.lastAdd.name,
        });
        if (addEffectTimer.current) clearTimeout(addEffectTimer.current);
        addEffectTimer.current = setTimeout(() => setAddEffect(null), ADD_EFFECT_MS);
      }
      prevRef.current = { id: data.id, addedSeconds: data.addedSeconds };

      zeroReloadedRef.current = false;
      setSub(data);
    } catch {
      // Lỗi mạng: giữ nguyên đang hiện, lần donate/tải dự phòng sau sẽ thử lại.
    }
  }, [contentCreatorId]);

  useEffect(() => {
    load();
  }, [load]);

  // Đồng hồ máy 1 giây — remainingAt tự trừ thời gian trôi kể từ serverNow (đã bù skew).
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(
    () => () => {
      if (addEffectTimer.current) clearTimeout(addEffectTimer.current);
      if (secondLoadTimer.current) clearTimeout(secondLoadTimer.current);
    },
    []
  );

  // Tải lại dự phòng: phòng khi hub rớt kết nối hoặc bỏ lỡ sự kiện.
  usePollWhile(!!contentCreatorId, load, FALLBACK_POLL_MS);

  useEffect(() => {
    if (!contentCreatorId) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection
      .start()
      .then(() => {
        connection.on('ReceiveDonationInfo', () => {
          load();
          // Sự kiện donate mới hơn -> huỷ lịch tải lần 2 cũ, đặt lịch mới theo sự kiện mới nhất.
          if (secondLoadTimer.current) clearTimeout(secondLoadTimer.current);
          secondLoadTimer.current = setTimeout(() => load(), 3500);
        });
      })
      .catch(() => {
        // Không nối được hub: vẫn hiện số ở lần tải đầu, dựa vào tải lại dự phòng.
      });

    return () => {
      connection.stop();
      if (secondLoadTimer.current) clearTimeout(secondLoadTimer.current);
    };
  }, [contentCreatorId, load]);

  const remaining = sub ? remainingAt(sub, now, skewRef.current) : 0;

  // Đồng hồ vừa chạm 0 lúc đang chạy nhưng máy chủ chưa chắc đã kịp lưu Ended (job đọc lười) ->
  // tải lại một lần để xác nhận trạng thái mới, tránh đứng yên ở "0:00" nếu máy chủ đã kết thúc.
  useEffect(() => {
    if (sub?.status === 'Running' && !sub.isPaused && remaining <= 0 && !zeroReloadedRef.current) {
      zeroReloadedRef.current = true;
      load();
    }
  }, [remaining, sub, load]);

  // Chưa có subathon nào (chưa từng bắt đầu, hoặc đã kết thúc quá 10 phút) -> BE trả
  // exists=false, không vẽ gì để OBS trong suốt hoàn toàn.
  if (!sub?.exists) return null;

  const endedRecently = isEndedVisible(sub, now, skewRef.current);

  if (sub.status === 'Ended' && !endedRecently) return null;

  const clockStyle = { color: `#${clockColor}`, textShadow: OUTLINE };
  const textStyle = { color: `#${textColor}`, textShadow: OUTLINE };

  return (
    <div className="flex h-screen w-full flex-col items-center justify-start bg-transparent p-4">
      <div className="w-full max-w-[560px] text-center">
        <div className="mb-1 truncate text-2xl font-extrabold" style={textStyle}>
          {sub.title}
        </div>

        {endedRecently ? (
          <div className="text-5xl font-extrabold" style={clockStyle}>
            Hết giờ!
          </div>
        ) : (
          <>
            <div className="text-6xl font-extrabold tabular-nums" style={clockStyle}>
              {formatHms(remaining)}
            </div>
            {sub.isPaused && (
              <div className="mt-1 text-xl font-semibold" style={textStyle}>
                Tạm dừng
              </div>
            )}
            <div className="mt-1 text-lg font-semibold" style={textStyle}>
              Cứ {Number(sub.caPerMinute).toLocaleString('vi-VN')} Ca thêm 1 phút
            </div>
          </>
        )}

        {addEffect && (
          <div className="mt-2 text-2xl font-extrabold" style={clockStyle}>
            {addEffect.label} · {addEffect.name}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSubathonLivestream;
